import { useCallback, useEffect, useMemo, useRef } from "react";
import { apiPatch } from "../lib/apiClient.js";
import { withoutOrderId } from "../lib/forecast/orderForecasts.js";

// Manual Forecast Manager ordering, persisted per-user account-side.
//
// The order lives in users.preferences.forecastOrder (loaded via /api/me into
// `preferences` by useAuthSession). This hook treats `preferences` as the single
// client source of truth: `order` is derived from it, and `saveOrder` updates it
// optimistically (setPreferences) AND persists to the server (debounced
// PATCH /api/me/preferences, best-effort). Because the save updates preferences
// in place, a later /api/me refetch echoes the same value — no clobber loop.
//
// @param {{ preferences: object, setPreferences: Function }} deps
// @returns {{ order: string[], saveOrder: (ids: string[]) => void,
//   removeFromOrder: (id: string) => void }}
export function useForecastOrder({ preferences, setPreferences }) {
  const order = useMemo(
    () => (Array.isArray(preferences?.forecastOrder) ? preferences.forecastOrder : []),
    [preferences],
  );
  const timerRef = useRef(null);
  // Clear a pending debounced PATCH on unmount so a stray save can't fire after
  // the component (and its preferences setter) are gone.
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );
  const saveOrder = useCallback(
    (ids) => {
      if (!Array.isArray(ids)) return;
      // Optimistic: reflect the new order immediately in the shared preferences
      // so the list re-renders without waiting on the server round-trip.
      setPreferences?.((p) => ({ ...(p || {}), forecastOrder: ids }));
      // Debounced persist — a burst of drags collapses to one PATCH. Best-effort:
      // a failed save just means the order isn't persisted this session (the
      // optimistic local order still applies); apiPatch never throws on network.
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        // Suppress apiPatch's default 401→/auth/login redirect: reordering is a
        // best-effort enhancement, and in auth-disabled / legacy-no-auth mode this
        // endpoint 401s (no session userId) — a background save must never navigate
        // the user away. The optimistic local order already applied regardless.
        // telemetry-no-intent: debounced persist; the drop that moved a card emits forecast.reorder
        apiPatch("/api/me/preferences", { forecastOrder: ids }, { onUnauthorized: () => {} });
      }, 400);
      // setPreferences is a stable useState setter from useAuthSession.
    },
    [setPreferences],
  );
  // Drop a deleted forecast from the SAVED order (preferences, not a derived display list,
  // which may be empty mid-boot); a no-op save is skipped when the id was never ordered.
  const removeFromOrder = useCallback(
    (id) => {
      const next = withoutOrderId(order, id);
      if (next !== order) saveOrder(next);
    },
    [order, saveOrder],
  );
  return { order, saveOrder, removeFromOrder };
}
